import { useCenterStore } from '@caradasstore/CenterStore'
import { AssigneeAvatarPicker } from '@components/assigned-user'
import { ItemPerPageSelector } from '@components/common'
import { Checkbox } from '@components/ui/checkbox'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@components/ui/table'
import { AdasBox, AdasCircularProgress, AdasPagination, AdasPaper, AdasStack, AdasTypography } from '@components/wrapper-components'
import { Globe, Search, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { searchQWIById } from '../../../../api/api'
import { getInvoiceSummaryUrl, getQuoteSummaryUrl, getWorkOrderSummaryUrl } from '../../../../constants'
import { StatusEnum } from '../../../../enums/enums'
import { Logger } from '../../../../logger'
import { currencyFormatter } from '../../../../utils/utils'

// The global search returns a mixed list of Quote / Work Order / Invoice records
// (SearchResponseDTO), each tagged by `table_name`. That drives the record-number
// prefix, the assignee resource (the `/v1/{type}/…` path segment), and the summary
// route to open on click.
const RECORD_TYPE_CONFIG = {
  quote: { prefix: 'Q #', assigneeType: 'quotes', getUrl: getQuoteSummaryUrl },
  workorder: { prefix: 'WO #', assigneeType: 'workorders', getUrl: getWorkOrderSummaryUrl },
  invoice: { prefix: 'Inv #', assigneeType: 'invoices', getUrl: getInvoiceSummaryUrl }
}

const getCfg = (item) => RECORD_TYPE_CONFIG[item?.table_name] ?? RECORD_TYPE_CONFIG.quote

// A record id is only unique within its own table, so key rows by table + id.
const rowKey = (item) => `${item.table_name}-${item.id}`

// The owning shop/center for the "Location" column. The DTO may carry the name
// directly or via the center object; otherwise fall back to the cached summaries.
const centerNameOf = (item, centerSummaries) =>
  item?.center_name ||
  item?.calibrationCenter?.name ||
  item?.calibration_center?.name ||
  centerSummaries?.[item?.calibration_center_id]?.name ||
  ''

const formatType = (t) => {
  if (!t) return null
  if (t === 'SERVICE_ONLY') return 'Service'
  if (t === 'STANDARD') return 'Standard'
  return t
}

const formatDate = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const date = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  return `${date} ${time}`
}

export const QwiSearchSettings = () => {
  const centerSummaries = useCenterStore((store) => store.centerSummaries)
  const history = useHistory()

  const [searchInput, setSearchInput] = useState('')
  const [query, setQuery] = useState('')
  const [allRows, setAllRows] = useState([])
  const [pageNo, setPageNo] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [loading, setLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [selected, setSelected] = useState({})

  // The endpoint returns every match at once, so paginate the result set client-side.
  const totalElements = allRows.length
  const totalPages = Math.ceil(allRows.length / pageSize)
  const rows = allRows.slice(pageNo * pageSize, pageNo * pageSize + pageSize)

  // Debounce typing before it becomes a search term (matches the Quotes queue's 350ms).
  // Require at least 3 characters before hitting the API — shorter input clears the query.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const trimmed = searchInput.trim()
      setQuery(trimmed.length >= 3 ? trimmed : '')
      setPageNo(0)
    }, 350)
    return () => clearTimeout(timeoutId)
  }, [searchInput])

  useEffect(() => {
    if (!query) {
      setAllRows([])
      setHasSearched(false)
      return
    }

    let cancelled = false
    setLoading(true)
    setHasSearched(true)

    // Single global lookup — the BE searches Quotes, Work Orders, and Invoices
    // across every center by record #, VIN, or RO, with no ccid (CAR-3666).
    searchQWIById({ searchId: query })
      .then((response) => {
        if (cancelled) return
        const list = Array.isArray(response?.data) ? [...response.data] : []
        // Newest first across every center and record type.
        list.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
        setAllRows(list)
        setSelected({})
        setPageNo(0)
        setLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        Logger.error({ message: error, payload: { file: 'QwiSearchSettings', method: 'search', query } })
        setAllRows([])
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [query])

  const paginationHandler = useCallback((event, page) => {
    setPageNo(page - 1)
  }, [])

  const pageSizeHandler = useCallback((event) => {
    setPageSize(event.target.value)
    setPageNo(0)
  }, [])

  const allSelected = rows.length > 0 && rows.every((item) => selected[rowKey(item)])

  const toggleAll = () => {
    if (allSelected) setSelected({})
    else setSelected(rows.reduce((acc, item) => ({ ...acc, [rowKey(item)]: true }), {}))
  }

  const toggleRow = (key) => {
    setSelected((prev) => {
      const next = { ...prev }
      if (next[key]) delete next[key]
      else next[key] = true
      return next
    })
  }

  const handleClick = (item) => {
    history.push(getCfg(item).getUrl(item?.reference_number))
  }

  return (
    <AdasBox className='mx-auto max-w-[1200px] mt-9'>
      {/* Page header */}
      <AdasBox className='mb-5'>
        <AdasBox className='flex flex-wrap items-center gap-3'>
          <AdasTypography className='text-[32px] font-bold leading-tight text-foreground'>QWI Search</AdasTypography>
          <span className='inline-flex items-center gap-1.5 rounded-full border border-secondary/30 bg-secondary/5 px-3 py-1 text-[13px] font-semibold uppercase tracking-wide text-secondary'>
            <Globe className='h-4 w-4' />
            All Locations
          </span>
        </AdasBox>
        <AdasTypography className='mt-1 text-[14px] text-muted-foreground'>
          Global search — finds a Quote, Work Order, or Invoice by record #, VIN, or RO # across every location.
        </AdasTypography>
      </AdasBox>

      {/* Search box */}
      <AdasBox className='mb-6'>
        <AdasBox className='relative'>
          <Search className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6B7280]' />
          <input
            type='text'
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder='Type at least 3 characters to search records across Quotes, Work Orders, and Invoices.'
            className='h-[40px] w-full rounded-lg border border-border bg-white pl-9 pr-9 text-[14px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-secondary/40'
          />
          {searchInput && (
            <button
              type='button'
              onClick={() => setSearchInput('')}
              aria-label='Clear search'
              className='absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] transition-colors hover:text-foreground'
            >
              <X className='h-4 w-4' />
            </button>
          )}
        </AdasBox>
        {searchInput.trim().length > 0 && searchInput.trim().length < 3 && (
          <AdasTypography className='mt-2 text-[13px] text-muted-foreground'>Enter at least 3 characters to search.</AdasTypography>
        )}
      </AdasBox>

      {loading && (
        <AdasBox className='flex items-center justify-center py-12'>
          <AdasCircularProgress size={28} />
        </AdasBox>
      )}

      {!loading && !query && (
        <AdasBox className='rounded-xl border border-dashed border-border bg-white p-10 text-center'>
          <Search className='mx-auto mb-3 h-6 w-6 text-muted-foreground' />
          <p className='text-[14px] text-muted-foreground'>
            Type at least 3 characters to search records across Quotes, Work Orders, and Invoices.
          </p>
        </AdasBox>
      )}

      {!loading && hasSearched && query && rows.length === 0 && (
        <AdasBox className='rounded-xl border border-border bg-white p-10 text-center'>
          <p className='text-[14px] text-muted-foreground'>No records found for “{query}”.</p>
        </AdasBox>
      )}

      {!loading && rows.length > 0 && (
        <>
          <AdasBox className='overflow-x-auto rounded-lg border border-[#D1D5D8] bg-white'>
            <Table>
              <TableHeader>
                <TableRow className='bg-gradient-to-r from-[#2C3D4D] to-[#1F2D3D] hover:from-[#2C3D4D] hover:to-[#1F2D3D]'>
                  <TableHead className='w-12 text-white'>
                    <Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label='Select all records' />
                  </TableHead>
                  <TableHead className='text-white text-[12px] font-semibold uppercase tracking-wide'>QWI #</TableHead>
                  <TableHead className='text-white text-[12px] font-semibold uppercase tracking-wide'>Client</TableHead>
                  <TableHead className='text-white text-[12px] font-semibold uppercase tracking-wide'>Location</TableHead>
                  <TableHead className='text-white text-[12px] font-semibold uppercase tracking-wide'>Assignee</TableHead>
                  <TableHead className='text-white text-[12px] font-semibold uppercase tracking-wide'>Status</TableHead>
                  <TableHead className='text-white text-[12px] font-semibold uppercase tracking-wide text-right'>Amount</TableHead>
                  <TableHead className='text-white text-[12px] font-semibold uppercase tracking-wide text-right'>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((item) => {
                  const isOpen = StatusEnum[item.status] === StatusEnum.OPEN
                  const key = rowKey(item)
                  const isSelected = !!selected[key]
                  const type_ = formatType(item.type)
                  const cfg = getCfg(item)
                  const centerName = centerNameOf(item, centerSummaries)
                  return (
                    <TableRow
                      key={key}
                      data-state={isSelected ? 'selected' : undefined}
                      onClick={() => handleClick(item)}
                      className='cursor-pointer h-[72px] even:bg-[#F8FAFC] hover:bg-[#F0F4FF] data-[state=selected]:bg-[#E8EFFF] data-[state=selected]:hover:bg-[#E8EFFF]'
                    >
                      <TableCell className='align-middle' onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleRow(key)}
                          aria-label={`Select ${cfg.prefix}${item.reference_number}`}
                        />
                      </TableCell>
                      <TableCell className='align-middle pr-4'>
                        <div className='w-[230px] max-w-[230px]'>
                          <div className='flex items-center gap-x-1.5 overflow-hidden text-[12px] text-[#6B7280] whitespace-nowrap'>
                            <span>
                              {cfg.prefix}
                              {item.reference_number}
                            </span>
                            {item.repair_order_num && (
                              <>
                                <span className='text-[#CBD5E1]'>|</span>
                                <span>RO #{item.repair_order_num}</span>
                              </>
                            )}
                            {type_ && (
                              <>
                                <span className='text-[#CBD5E1]'>|</span>
                                <span>{type_}</span>
                              </>
                            )}
                            {item.insurance_company?.name && (
                              <>
                                <span className='text-[#CBD5E1]'>|</span>
                                <span>{item.insurance_company.name}</span>
                              </>
                            )}
                          </div>
                          <div className='mt-1 truncate text-[15px] font-bold uppercase tracking-tight text-[#0A1628]'>
                            {[item.vehicle?.year, item.vehicle?.make, item.vehicle?.model].filter(Boolean).join(' ') || '—'}
                          </div>
                          {item.vehicle?.vin && (
                            <div className='mt-0.5 truncate font-mono text-[11px] text-[#6B7280] tracking-wider'>{item.vehicle.vin}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className='align-middle text-[13px] text-[#0A1628]'>
                        <div className='max-w-[130px] truncate'>{item.client?.name || '—'}</div>
                      </TableCell>
                      <TableCell className='align-middle text-[13px] text-[#0A1628]'>
                        <div className='max-w-[160px] truncate' title={centerName}>
                          {centerName || '—'}
                        </div>
                      </TableCell>
                      <TableCell className='align-middle' onClick={(e) => e.stopPropagation()}>
                        <AssigneeAvatarPicker
                          type={cfg.assigneeType}
                          workOrderId={item.id}
                          initialAssignee={item.assigned_to}
                          closedStatus={StatusEnum[item.status] === StatusEnum.CLOSED}
                        />
                      </TableCell>
                      <TableCell className='align-middle'>
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase ${
                            isOpen ? 'bg-[#E8EFFF] text-[#2C3D4D]' : 'bg-[#EBEDF0] text-[#6B7280]'
                          }`}
                        >
                          <span className={`inline-block h-1.5 w-1.5 rounded-full ${isOpen ? 'bg-[#2C3D4D]' : 'bg-[#6B7280]'}`} />
                          {StatusEnum[item.status]}
                        </span>
                      </TableCell>
                      <TableCell className='align-middle text-right text-[15px] font-bold text-[#0A1628] whitespace-nowrap'>
                        {currencyFormatter(item.total_cost)}
                      </TableCell>
                      <TableCell className='align-middle text-right text-[13px] text-[#6B7280] whitespace-nowrap'>
                        {formatDate(item.created_at)}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </AdasBox>

          <AdasPaper elevation={0} className='mt-5 flex items-center justify-between rounded-lg border border-[#D1D5D8] bg-white p-3'>
            <AdasBox className='w-[160px]'>
              <ItemPerPageSelector itemPerPageList={[25, 50, 100, 200]} pageSizeHandler={pageSizeHandler} pageSize={pageSize} />
            </AdasBox>
            <AdasBox className='flex items-center gap-4'>
              <span className='hidden text-[13px] text-muted-foreground sm:inline'>
                {totalElements} result{totalElements === 1 ? '' : 's'}
              </span>
              <AdasStack spacing={2}>
                <AdasPagination onChange={paginationHandler} page={pageNo + 1} count={totalPages} shape='rounded' />
              </AdasStack>
            </AdasBox>
          </AdasPaper>
        </>
      )}
    </AdasBox>
  )
}
